/**
 * WorkspaceSwitcher — dropdown that lists workspaces the caller belongs to
 * and lets them switch active workspace or start creating a new one.
 */
import { Check, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useCurrentWorkspace,
  useWorkspaceStore,
  WorkspaceMonogram,
} from "@/entities/workspace";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";

export function WorkspaceSwitcher(): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const current = useCurrentWorkspace();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="workspace-switcher-trigger"
          variant="outline"
          className="h-11 gap-3 rounded-lg border-border/70 bg-background/80 pl-2 pr-3 backdrop-blur"
        >
          <WorkspaceMonogram workspace={current} size="md" />
          <div className="flex flex-col items-start leading-tight">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Workspace
            </span>
            <span
              data-testid="workspace-switcher-current"
              className="text-sm font-semibold"
            >
              {current?.profile.display_name ?? "Select a workspace"}
            </span>
          </div>
          <ChevronDown className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-[280px]"
        data-testid="workspace-switcher-menu"
      >
        <DropdownMenuLabel className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          Your businesses
        </DropdownMenuLabel>
        {workspaces.map((w) => {
          const isCurrent = current?.id === w.id;
          return (
            <DropdownMenuItem
              key={w.id}
              data-testid={`workspace-option-${w.slug}`}
              onSelect={() => selectWorkspace(w.id)}
              className="gap-3 py-2.5"
            >
              <WorkspaceMonogram workspace={w} size="sm" />
              <div className="flex flex-1 flex-col leading-tight">
                <span className="text-sm font-medium">
                  {w.profile.display_name}
                </span>
                {w.profile.tagline ? (
                  <span className="text-[11px] text-muted-foreground">
                    {w.profile.tagline}
                  </span>
                ) : null}
              </div>
              {isCurrent ? (
                <Check className="h-4 w-4 text-accent" />
              ) : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="workspace-switcher-create"
          onSelect={() => {
            setOpen(false);
            navigate("/workspaces/new");
          }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          <span className="text-sm font-medium">New workspace</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
